import { useState } from 'react';

import {
  contactCustomPayload,
  initialCustomValues,
  manualFromContact,
  validateCustomValues,
  validateManualFields,
  type CustomFieldTemplate,
  type CustomValue,
  type CustomValues,
  type ManualFieldDraft,
} from './customFields';

interface SavedContact {
  custom_field_template_id?: number | string | null;
  additional_details?: Record<string, unknown> | null;
  custom_field_defs?: unknown;
}

/**
 * Custom field state of one contact form: the chosen template, its values,
 * and the fields added on this contact only. Other keys in additional_details
 * (e.g. disposition answers) are not part of the form.
 *
 * valuesOnly (agent / team-lead panel): fields already on the contact keep their name and type
 * and cannot be removed — only their values change; the contact's template cannot be swapped.
 */
export function useContactCustomFieldsForm(
  templates: CustomFieldTemplate[],
  loading = false,
  { valuesOnly = false }: { valuesOnly?: boolean } = {},
) {
  const [template, setTemplate] = useState<CustomFieldTemplate | null>(null);
  const [values, setValues] = useState<CustomValues>({});
  const [manual, setManual] = useState<ManualFieldDraft[]>([]);
  const [saved, setSaved] = useState<SavedContact | null>(null);

  /** Fill from a saved contact (edit), or clear (create). */
  const load = (contact: SavedContact | null) => {
    const t = contact ? templates.find((x) => x.id === Number(contact.custom_field_template_id)) || null : null;
    setSaved(contact);
    setTemplate(t);
    setValues(initialCustomValues(t, contact?.additional_details));
    const added = manualFromContact(contact?.custom_field_defs, contact?.additional_details);
    setManual(valuesOnly ? added.map((m) => ({ ...m, locked: true })) : added);
  };

  const validate = () => validateCustomValues(template, values) || validateManualFields(manual, template);

  // Edit: the contact had a template and None, or another template, is now selected.
  const savedTemplateId = Number(saved?.custom_field_template_id) || 0;
  const removingTemplate = !template && savedTemplateId > 0;
  const switchingTemplate = !!template && savedTemplateId > 0 && template.id !== savedTemplateId;
  const removedTemplateName = removingTemplate || switchingTemplate
    ? templates.find((t) => t.id === savedTemplateId)?.name || 'the current template'
    : null;

  /** Body fields to merge into the create / update request. */
  const payload = (isEdit: boolean) => ({
    ...contactCustomPayload(template, values, manual, isEdit),
    ...(isEdit && removingTemplate ? { remove_custom_field_template: true } : {}),
  });

  const sectionProps = {
    templates,
    loading,
    templateId: template?.id ?? null,
    onTemplateChange: (t: CustomFieldTemplate | null) => {
      setTemplate(t);
      setValues(initialCustomValues(t, saved?.additional_details));
    },
    values,
    onValueChange: (fieldName: string, v: CustomValue) => setValues((prev) => ({ ...prev, [fieldName]: v })),
    manualFields: manual,
    onManualChange: setManual,
    valuesOnly,
    templateNotice: removedTemplateName
      ? switchingTemplate
        ? `On save, the field values of "${removedTemplateName}" will be replaced with this template's values.`
        : `On save, "${removedTemplateName}" and its field values will be removed from this contact.`
      : null,
  };

  return { load, validate, payload, sectionProps };
}
